import { useState } from 'react';
import { Building2, ChevronDown, ChevronRight, Network, Search, User, UserMinus, Users } from '../../../icons';
import type { MenuAccessState } from '../../../api/menu-access';
import { peopleOf, rulesOf, sameUnit, type OrgModel, type Unit } from './model';

/**
 * 조직 트리 — 회사 · 본부 · 팀 · 본부 없는 팀 · 팀없는직원 (확정 시안 v4 · A8 ListPane 골격 · 정본 제안 G4).
 *
 * ★ 줄 끝 점 = 이 단위에 규칙이 있다(회사는 끈 메뉴). 팀없는직원의 점은 «예전 묶음 규칙»이라 색이 다르다.
 * ★ 찾기는 조직 이름과 **사람 이름**을 함께 본다 — 사람을 누르면 그 사람의 개인별 권한으로 간다.
 */
interface Props {
  org: OrgModel | null;
  orgFailed: boolean;
  state: MenuAccessState;
  unit: Unit;
  onSelectUnit: (u: Unit) => void;
  onJumpPerson: (personId: string) => void;
}

export function OrgTree({ org, orgFailed, state, unit, onSelectUnit, onJumpPerson }: Props) {
  const [q, setQ] = useState('');
  const [folded, setFolded] = useState<Set<string>>(new Set());

  const ruleCount = (u: Unit): number => {
    if (u.kind === 'company') return state.company.filter((c) => !c.enabled).length;
    if (u.kind === 'unassigned') return rulesOf(state, 'unassigned', 'all').length;
    return rulesOf(state, u.kind, u.id).length;
  };
  const count = (u: Unit): number => (org ? peopleOf(org, u).length : 0);

  const row = (u: Unit, opts: { label: string; icon: JSX.Element; depth: number; caret?: string }) => {
    const on = sameUnit(unit, u);
    const rc = ruleCount(u);
    const isFolded = opts.caret ? folded.has(opts.caret) : false;
    return (
      <div className={`mpx-tree__row mpx-tree__row--d${opts.depth}${on ? ' is-on' : ''}`}>
        {opts.caret ? (
          <button
            type="button" className="mpx-tree__caret"
            aria-label={`${opts.label} ${isFolded ? '펼치기' : '접기'}`} aria-expanded={!isFolded}
            onClick={() => setFolded((s) => {
              const n = new Set(s);
              if (n.has(opts.caret!)) n.delete(opts.caret!); else n.add(opts.caret!);
              return n;
            })}
          >
            {isFolded ? <ChevronRight size={13} aria-hidden /> : <ChevronDown size={13} aria-hidden />}
          </button>
        ) : <span className="mpx-tree__caret" aria-hidden />}
        <button type="button" className="mpx-tree__item" aria-current={on || undefined} onClick={() => onSelectUnit(u)}>
          <span className="mpx-tree__icon" aria-hidden>{opts.icon}</span>
          <span className="mpx-tree__name">{opts.label}</span>
          {rc > 0 && (
            <span
              className={`mpx-tree__dot${u.kind === 'unassigned' ? ' mpx-tree__dot--old' : ''}`}
              title={u.kind === 'unassigned' ? `예전 묶음 규칙 ${rc}개 — 판정에 안 씀` : `규칙 ${rc}개`}
            />
          )}
          <span className="mpx-tree__count">{count(u)}</span>
        </button>
      </div>
    );
  };

  const needle = q.trim();
  return (
    <aside className="mpx-tree" aria-label="조직">
      <div className="mpx-tree__head">
        <b>조직</b>
        {org && <span>{org.people.length}명</span>}
      </div>
      <div className="mpx-find">
        <Search size={14} aria-hidden />
        <input
          id="mpx-tree-q" type="search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="본부 · 팀 · 이름 찾기" aria-label="조직 · 사람 찾기"
        />
      </div>
      <nav className="mpx-tree__nav">
        {needle && org ? (
          <>
            {(() => {
              const units: Array<{ u: Unit; label: string; icon: JSX.Element }> = [
                ...org.divisions.filter((d) => d.name.includes(needle))
                  .map((d) => ({ u: { kind: 'division', id: d.id } as Unit, label: d.name, icon: <Network size={14} /> })),
                ...[...org.teams.values()].filter((t) => t.name.includes(needle))
                  .map((t) => ({ u: { kind: 'team', id: t.id } as Unit, label: t.name, icon: <Users size={14} /> })),
              ];
              const people = org.people.filter((p) => p.name.includes(needle));
              return (
                <>
                  <p className="mpx-tree__lbl">조직 {units.length}</p>
                  {units.map((x) => <div key={`${x.u.kind}-${x.label}`}>{row(x.u, { label: x.label, icon: x.icon, depth: 0 })}</div>)}
                  <p className="mpx-tree__lbl">사람 {people.length} — 누르면 개인별 권한으로</p>
                  {people.map((p) => (
                    <div key={p.id} className="mpx-tree__row mpx-tree__row--d0">
                      <span className="mpx-tree__caret" aria-hidden />
                      <button type="button" className="mpx-tree__item" onClick={() => onJumpPerson(p.id)}>
                        <span className="mpx-tree__icon" aria-hidden><User size={14} /></span>
                        <span className="mpx-tree__name">{p.name}{p.position ? <span className="mpx-dim"> {p.position}</span> : null}</span>
                        <span className="mpx-tree__count">{p.judgeTeamId ? org.teams.get(p.judgeTeamId)?.name ?? '' : '팀 없음'}</span>
                      </button>
                    </div>
                  ))}
                  {units.length === 0 && people.length === 0 && (
                    <p className="mpx-tree__lbl">「{needle}」에 맞는 조직 · 사람이 없습니다.</p>
                  )}
                </>
              );
            })()}
          </>
        ) : (
          <>
            {row({ kind: 'company' }, { label: '회사 전체', icon: <Building2 size={14} />, depth: 0 })}
            {orgFailed && <p className="mpx-tree__lbl">조직을 불러오지 못했습니다 — 회사 설정만 고칠 수 있습니다.</p>}
            {org && org.divisions.map((d) => (
              <div key={d.id}>
                {row({ kind: 'division', id: d.id }, { label: d.name, icon: <Network size={14} />, depth: 1, caret: d.id })}
                {!folded.has(d.id) && d.teamIds.map((tid) => (
                  <div key={tid}>{row({ kind: 'team', id: tid }, { label: org.teams.get(tid)?.name ?? '팀', icon: <Users size={14} />, depth: 2 })}</div>
                ))}
              </div>
            ))}
            {org && org.looseTeamIds.length > 0 && (
              <>
                <p className="mpx-tree__lbl">본부 없는 팀</p>
                {org.looseTeamIds.map((tid) => (
                  <div key={tid}>{row({ kind: 'team', id: tid }, { label: org.teams.get(tid)?.name ?? '팀', icon: <Users size={14} />, depth: 1 })}</div>
                ))}
              </>
            )}
            {org && (
              <>
                <p className="mpx-tree__lbl">배정 전</p>
                {row({ kind: 'unassigned' }, { label: '팀없는직원', icon: <UserMinus size={14} />, depth: 1 })}
              </>
            )}
          </>
        )}
      </nav>
      <div className="mpx-tree__foot" aria-label="구분">
        <span><Building2 size={12} aria-hidden /> 회사</span>
        <span><Network size={12} aria-hidden /> 본부</span>
        <span><Users size={12} aria-hidden /> 팀</span>
        <span><UserMinus size={12} aria-hidden /> 팀없는직원</span>
        <span><i className="mpx-tree__dot" aria-hidden /> 규칙 있음</span>
      </div>
    </aside>
  );
}
